import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserView } from '@retro/shared';
import { ApiError, api } from './api.js';

/**
 * The signed-in user, or null. A 401 is an expected answer here (guests and
 * visitors have no session), so it resolves to null instead of throwing.
 */
export function useCurrentUser() {
  return useQuery<UserView | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const { user } = await api.auth.me();
        return user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Public instance settings. Needed before anyone is signed in, so that a
 * closed instance does not offer a registration form that can only fail.
 */
export function usePublicConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: api.config.get,
    // Set at boot on the server, so there is no point re-fetching it.
    staleTime: Infinity,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.auth.login,
    onSuccess: ({ user }) => {
      queryClient.setQueryData(['me'], user);
      // The teams query fires while signed out and caches the empty answer —
      // without this the shell keeps showing "no teams" after signing in.
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.auth.register,
    onSuccess: ({ user }) => {
      queryClient.setQueryData(['me'], user);
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.auth.logout,
    onSuccess: () => {
      queryClient.setQueryData(['me'], null);
      // Clear cached team/retro data belonging to the previous session.
      queryClient.clear();
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.auth.updateProfile,
    onSuccess: ({ user }) => {
      queryClient.setQueryData(['me'], user);
      // The name and avatar are copied into team member lists, assignee pickers
      // and the admin table, so refetch rather than trying to patch each one.
      void queryClient.invalidateQueries();
    },
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: api.auth.changePassword });
}
